import { pick } from 'lodash'
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { graphql } from 'app/generated/gql'

import { useUser, useWorkspace } from 'app/utils'
import { hasInboxProduct } from 'app/components/inbox/useInboxLocations'
import { INBOX_LOCATIONS_QUERY, type InboxLocationsResult } from 'app/graphql/inboxLocations'
import { SIDEBAR_LISTS_QUERY } from 'app/graphql/lists'
import { fetchUnreadOptOutSuppression, isUnreadSuppressed, type UnreadCountableMessage } from 'app/lib/unreadOptOuts'
import { gqlQuery } from 'app/lib/graphqlClient'
import { useMessageEvents } from 'app/hooks/useMessageEvents'
import { FilterObject } from 'app/filter.utils'
import { HYGIENE_DUE_SOON_TAB, HYGIENE_OVERDUE_TAB, hygieneTabFilterObjects } from 'app/lib/hygieneTabs'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { legacyRoleRank } from 'app/types/roles'
import { myRoleId, myTasksSearch as buildMyTasksSearch } from 'lib/taskAssignee'
import { useAuthStore } from 'app/stores/StoreContext'
import { ACTIVITIES_TYPE_EXCLUSION } from 'lib/activitiesExclusion'
import { appointmentPageFilters, appointmentsSearch } from 'lib/appointmentsPage'
import { bg } from 'lib/background'
import { isCompanionPipeline } from 'lib/companionPipelines'
import { buildCountQuery, buildListCountQuery, readCounts } from 'lib/countQuery'
import { filtersToSearch } from 'lib/listSearch'
import { onSettledListWrite } from 'lib/listWrites'
import { debug, logError } from 'lib/logging'
import { isFilterList, isPracticeList, listRuleFilters } from 'lib/practiceLists'
import { LIST_ENTITY_TYPE } from 'lib/savedLists'
import { SYSTEM_LISTS, TREATMENT_LIST, familySearchTerms, toBookStatusTerms } from 'lib/systemLists'

const APPOINTMENTS_UPCOMING_KEY = `appointments-upcoming`
const TREATMENT_PLANS_KEY = `treatment-plans`

const SIDEBAR_COUNTS_QUERY = graphql(`
  query SidebarCounts(
    $activeLeadsSearch: String
    $activitiesSearch: String
    $scheduledLeadsSearch: String
    $closedLostLeadsSearch: String
    $dueForHygieneSearch: String
    $dueIn30DaysSearch: String
    $openTasksSearch: String
    $myTasksSearch: String
    $allTasksSearch: String
  ) {
    allContacts: contacts(pagination: { limit: 0 }) {
      total
    }
    allActivities: activities(search: $activitiesSearch, pagination: { limit: 0 }) {
      total
    }
    activeLeads: contacts(search: $activeLeadsSearch, pagination: { limit: 0 }) {
      total
    }
    scheduledLeads: contacts(search: $scheduledLeadsSearch, pagination: { limit: 0 }) {
      total
    }
    closedLostLeads: contacts(search: $closedLostLeadsSearch, pagination: { limit: 0 }) {
      total
    }
    dueForHygiene: contacts(search: $dueForHygieneSearch, pagination: { limit: 0 }) {
      total
    }
    dueIn30Days: contacts(search: $dueIn30DaysSearch, pagination: { limit: 0 }) {
      total
    }
    openTasks: tasks(search: $openTasksSearch, pagination: { limit: 0 }) {
      total
    }
    myTasks: tasks(search: $myTasksSearch, pagination: { limit: 0 }) {
      total
    }
    allTasks: tasks(search: $allTasksSearch, pagination: { limit: 0 }) {
      total
    }
    allWorkspaces: workspaces(pagination: { limit: 0 }) {
      total
    }
    allLocations: locations(pagination: { limit: 0 }) {
      total
    }
  }
`)

/**
 * The typed document is validated against the generated schema during codegen.
 *
 * The trailing `NOT status:pending` is the held-message exclusion, spelled out
 * here instead of coming from `excludeHeld()` in `app/lib/heldMessages`. This is
 * the one place in the app that cannot use that helper: a `graphql()` document
 * is plucked from source at build time, so its search string has to be a
 * literal and cannot call anything. `EXCLUDE_HELD` carries the matching note.
 *
 * It is deliberately a partial copy. `EXCLUDE_HELD` also excludes
 * `status:cancelled`, and this query needs neither that clause nor the pending
 * one: it counts UNREAD INBOUND messages, and a held message fails two of the
 * clauses already here on its own. It is outbound, so it carries either
 * `sentByUserId` or `isAgent`, and every Message create path stamps
 * `read: true`, so it can never match `!read:true`. The pending clause stays as
 * belt and braces; there is nothing to add.
 */
const UNREAD_MESSAGES_QUERY = graphql(`
  query UnreadInboxMessages {
    messages(
      search: "!read:true AND NOT archived:true AND !sentByUserId:* AND !isAgent:true AND NOT status:pending"
      pagination: { limit: 500 }
    ) {
      hits {
        threadId
        locationId
        from
      }
    }
  }
`)

/**
 * Unread *threads* the inbox can actually show, deduped by thread id.
 *
 * The unread search is org-wide, but the inbox only lists locations carrying the
 * Inbox product (see `inboxEnabledLocations`). Counting the rest produced a badge
 * with no thread behind it: nothing in the UI could open or clear those threads,
 * so the count sat there permanently.
 *
 * `suppression` is the same exclusion for opted-out threads, which the list drops
 * from every working filter (see `fetchUnreadOptOutSuppression`).
 */
export function countUnreadThreads(
  hits: Array<UnreadCountableMessage>,
  inboxLocationIds: Set<string>,
  suppression: ReadonlySet<string> = new Set(),
): number {
  const threadIds = new Set<string>()
  for (const hit of hits) {
    if (!hit.threadId || !hit.locationId) continue
    if (!inboxLocationIds.has(hit.locationId)) continue
    if (isUnreadSuppressed(hit, suppression)) continue
    threadIds.add(hit.threadId)
  }
  return threadIds.size
}

const CONTACT_CUSTOM_PAGES_QUERY = graphql(`
  query ContactSidebarCustomPages {
    customPages(search: "type:contacts", pagination: { limit: 1000 }) {
      hits {
        id
        orgId
        userId
        name
        page
        join
        filters {
          attribute
          secondaryAttribute
          displayName
          operator
          value
          secondaryValue
          static
          uniqOperator
          readOnly
          nonEmpty
          useAsterisk
          group
          subQuery
          type
          defaultValue
          dateOperator
          includeToday
          includeYesterday
          includeWeeks
          limitToMonth
          includeInThePast
          options
        }
      }
    }
  }
`)

const ACTIVITY_CUSTOM_PAGES_QUERY = graphql(`
  query ActivitySidebarCustomPages {
    customPages(search: "type:activities", pagination: { limit: 1000 }) {
      hits {
        id
        orgId
        userId
        name
        page
        join
        filters {
          attribute
          secondaryAttribute
          displayName
          operator
          value
          secondaryValue
          static
          uniqOperator
          readOnly
          nonEmpty
          useAsterisk
          group
          subQuery
          type
          defaultValue
          dateOperator
          includeToday
          includeYesterday
          includeWeeks
          limitToMonth
          includeInThePast
          options
        }
      }
    }
  }
`)

const TASK_CUSTOM_PAGES_QUERY = graphql(`
  query TaskSidebarCustomPages {
    customPages(search: "type:tasks", pagination: { limit: 1000 }) {
      hits {
        id
        orgId
        userId
        name
        page
        join
        filters {
          attribute
          secondaryAttribute
          displayName
          operator
          value
          secondaryValue
          static
          uniqOperator
          readOnly
          nonEmpty
          useAsterisk
          group
          subQuery
          type
          defaultValue
          dateOperator
          includeToday
          includeYesterday
          includeWeeks
          limitToMonth
          includeInThePast
          options
        }
      }
    }
  }
`)

const SIDEBAR_PIPELINES_QUERY = graphql(`
  query SidebarPipelines {
    pipelines {
      id
      name
    }
  }
`)

export type CountsData = {
  contacts?: Record<string, number>
  tasks?: Record<string, number>
  activities?: Record<string, number>
  appointments?: Record<string, number>
  treatmentPlans?: Record<string, number>
  pipelines?: Record<string, number>
  lists?: Record<string, number>
  workspaces?: Record<string, number>
  locations?: Record<string, number>
  hasOverdueMyTasks?: boolean
  inbox?: number
}

type SidebarCountsContextType = {
  counts: CountsData
  refresh: () => void
  refreshInboxCount: () => void
  adjustInboxCount: (delta: number) => void
}

type CountResult = { total: number }

type SidebarCountsQuery = {
  allContacts: CountResult
  allActivities: CountResult
  activeLeads: CountResult
  scheduledLeads: CountResult
  closedLostLeads: CountResult
  dueForHygiene: CountResult
  dueIn30Days: CountResult
  openTasks: CountResult
  myTasks: CountResult
  allTasks: CountResult
  allWorkspaces: CountResult
  allLocations: CountResult
}

type SidebarCustomPage = {
  id: string
  filters?: FilterObject[] | string | null
  join?: string | null
}

type CustomPagesQuery = {
  customPages: {
    hits: SidebarCustomPage[]
  }
}

const activityTagPages: Record<string, string> = {
  'missed-call': `Missed Call`,
  callback: `Callback`,
  voicemail: `Voicemail`,
  'not-scheduled': `Appointment Not Scheduled`,
  canceled: `Canceled Appointment`,
  scheduled: `Appointment Scheduled`,
  rescheduled: `Appointment Rescheduled`,
  confirmation: `Appointment Confirmation`,
}

function normalizeFilters(filters: SidebarCustomPage[`filters`]): FilterObject[] {
  if (Array.isArray(filters)) return filters
  if (typeof filters !== `string`) return []
  try {
    const parsed = JSON.parse(filters) as unknown
    return Array.isArray(parsed) ? (parsed as FilterObject[]) : []
  } catch (err) {
    logError(`Error parsing sidebar custom page filters`, err)
    return []
  }
}

const SidebarCountsContext = createContext<SidebarCountsContextType>({
  counts: {},
  refresh: () => {},
  refreshInboxCount: () => {},
  adjustInboxCount: () => {},
})

export function SidebarCountsProvider({ children }: { children: ReactNode }) {
  const user = useUser()
  const workspace = useWorkspace()
  const { products } = useAuthStore()

  if (!user) return <></>
  const [counts, setCounts] = useState<CountsData>({})
  // Last known server-reported inbox unread count. We only stomp the displayed
  // count when the server's reported value *changes* between polls — that's the
  // signal that something real shifted (our mutation finally reindexed, or
  // somebody/something else changed state). When the server's count is unchanged
  // we leave the local (possibly optimistic) value alone, so search-index lag
  // never flickers the badge back to zero.
  const inboxServerCountRef = useRef<number | null>(null)
  // One unread fetch in flight at a time. The shared clock ticks and pokes no
  // matter how slow the last request was, and refresh()/refreshInboxCount() can
  // fire between ticks — two overlapping fetches can resolve out of order, and
  // the stale count would land last and stomp the newer one.
  const unreadFetchInFlightRef = useRef(false)
  const inboxLocationIdsRef = useRef<Set<string> | null>(null)
  const needsActionDateEnabled = useFeatureToggle(`needs-action-date`)
  const groupedNavigationEnabled = useFeatureToggle(`grouped-navigation`)

  // Check if inbox is enabled for this workspace
  const inboxEnabled = !!(products?.includes(`Inbox`) && user.workspaceId)

  const loadCounts = () => {
    if (!user?.id) return
    // One read of the Lists per refresh, shared by the practice list counts and the saved list counts.
    const fetchSidebarLists = () => gqlQuery(SIDEBAR_LISTS_QUERY, {})
    let sidebarLists: ReturnType<typeof fetchSidebarLists> | undefined
    const readSidebarLists = () => (sidebarLists ??= fetchSidebarLists())
    // Under the grouped navigation every saved list is a List; contact Lists are counted with the practice lists.
    const savedListsToCount = async (
      pageType: `contacts` | `activities` | `tasks`,
      customPagesQuery: Parameters<typeof gqlQuery<CustomPagesQuery>>[0],
    ): Promise<SidebarCustomPage[]> => {
      if (!groupedNavigationEnabled)
        return (await gqlQuery<CustomPagesQuery>(customPagesQuery)).data?.customPages.hits ?? []
      if (pageType === `contacts`) return []
      // A failed read is reported where the practice list counts read it; here it counts nothing.
      const lists = (await readSidebarLists().catch(() => undefined))?.data?.lists ?? []
      return lists
        .filter((list) => list.entityType === LIST_ENTITY_TYPE[pageType] && !list.system)
        .map((list) => ({ id: list.id, filters: listRuleFilters(list), join: list.join }))
    }
    debug(`sidebar-counts`, `Refreshing sidebar counts...`)
    const activeDateField = needsActionDateEnabled ? `needsActionDate` : `becameLeadDate`
    const activeLeadsSearch = filtersToSearch([
      { attribute: `leadStatus.keyword`, value: [`Needs Action`, `In Progress`], operator: `in` },
      { attribute: activeDateField, value: `last_30_days`, operator: `between` },
      { attribute: `patientStatus`, value: [`NonPatient`], operator: `not_in` },
    ] as FilterObject[])
    const scheduledLeadsSearch = filtersToSearch([
      { attribute: `leadStatus.keyword`, value: [`Scheduled`], operator: `in` },
      {
        attribute: `nearestAppointmentDate`,
        value: new Date().toISOString().split(`T`)[0],
        operator: `greater_equal_than`,
      },
    ] as FilterObject[])
    const closedLostLeadsSearch = filtersToSearch([
      { attribute: `leadStatus.keyword`, value: [`Closed Lost`], operator: `in` },
    ] as FilterObject[])
    const dueForHygieneSearch = filtersToSearch(hygieneTabFilterObjects(HYGIENE_OVERDUE_TAB))
    const dueIn30DaysSearch = filtersToSearch(hygieneTabFilterObjects(HYGIENE_DUE_SOON_TAB))
    const openTasksSearch = filtersToSearch([
      { attribute: `status.keyword`, value: `open`, operator: `exactly` },
    ] as FilterObject[])
    const myTasksSearch = buildMyTasksSearch(user.id, myRoleId(user, user.workspaceId ?? undefined))
    // "All tasks" count — no filter, so it's just total tasks the user
    // can see. Replaced the prior `completedTasksSearch` (status:closed).
    const allTasksSearch = ``
    const inQueueCallTerm =
      user.role !== legacyRoleRank.orgAdmin ? ` AND NOT tags:(Call in Queue) AND NOT description:(Call in Queue)` : ``
    const activitiesSearch = `${ACTIVITIES_TYPE_EXCLUSION}${inQueueCallTerm}`
    bg(
      gqlQuery<SidebarCountsQuery>(SIDEBAR_COUNTS_QUERY, {
        activeLeadsSearch,
        activitiesSearch,
        scheduledLeadsSearch,
        closedLostLeadsSearch,
        dueForHygieneSearch,
        dueIn30DaysSearch,
        openTasksSearch,
        myTasksSearch,
        allTasksSearch,
      })
        .then(async ({ data }) => {
          if (!data) return
          debug(`sidebar-counts`, `Received counts data:`, data)
          setCounts((prev) => ({
            ...prev,
            contacts: {
              all: data.allContacts.total,
              [`active-leads`]: data.activeLeads.total,
              [`scheduled-leads`]: data.scheduledLeads.total,
              [`closed-lost-leads`]: data.closedLostLeads.total,
              [HYGIENE_OVERDUE_TAB]: data.dueForHygiene.total,
              [HYGIENE_DUE_SOON_TAB]: data.dueIn30Days.total,
            },
            activities: {
              all: data.allActivities.total,
            },
            workspaces: {
              all: data.allWorkspaces.total,
            },
            locations: {
              all: data.allLocations.total,
            },
            tasks: {
              [`open-tasks`]: data.openTasks.total,
              [`my-tasks`]: data.myTasks.total,
              [`all-tasks`]: data.allTasks.total,
            },
          }))
          if (groupedNavigationEnabled) {
            const systemListQuery = buildCountQuery(`SidebarSystemListCounts`, [
              ...SYSTEM_LISTS.map((def) => ({
                key: def.slug,
                search: [
                  ...familySearchTerms(def, undefined, { needsActionDateEnabled: !!needsActionDateEnabled }),
                  ...toBookStatusTerms(def),
                ].join(` `),
              })),
              {
                key: TREATMENT_PLANS_KEY,
                // The records page shows every case, Lost included, so it counts without the list's hidden terms.
                search: familySearchTerms(TREATMENT_LIST, undefined, {
                  needsActionDateEnabled: !!needsActionDateEnabled,
                }).join(` `),
              },
              {
                key: APPOINTMENTS_UPCOMING_KEY,
                entity: `appointments`,
                // The page's Upcoming search with no location, so the row and the page's total agree when the filter is clear.
                search: appointmentsSearch({
                  when: `upcoming`,
                  today: new Date(),
                  filtersParam: null,
                  pageFilters: appointmentPageFilters(`upcoming`, new Date(), []),
                }),
              },
            ])
            if (systemListQuery) {
              // network-only: a list write returns no typename this query caches, so cache-first would replay old counts.
              const systemListResult = await gqlQuery<Record<string, CountResult>>(
                systemListQuery.query,
                systemListQuery.variables,
                { requestPolicy: `network-only` },
              )
              // Read by bracket access, not computed-key destructuring: the latter crashes
              // oxlint's react-compiler InferMutationAliasingEffects pass inside this setCounts closure.
              const allSystemListCounts = readCounts(systemListResult.data, systemListQuery.aliases)
              const upcomingAppointments = allSystemListCounts[APPOINTMENTS_UPCOMING_KEY]
              const treatmentPlanCount = allSystemListCounts[TREATMENT_PLANS_KEY]
              const {
                [APPOINTMENTS_UPCOMING_KEY]: _upcoming,
                [TREATMENT_PLANS_KEY]: _treatmentPlans,
                ...systemListCounts
              } = allSystemListCounts
              setCounts((prev) => ({
                ...prev,
                contacts: { ...prev.contacts, ...systemListCounts },
                treatmentPlans: { all: treatmentPlanCount },
                appointments: { upcoming: upcomingAppointments },
              }))
            }
            // Its own try so a failed list read cannot skip the custom page and pipeline counts below.
            try {
              const listsResult = await readSidebarLists()
              if (!listsResult.data) {
                logError(`Error fetching practice lists for sidebar counts`, listsResult.error)
              } else {
                // The typed document returns `system` as `boolean | null`; the predicate takes the generated base type.
                const practiceLists = listsResult.data.lists.filter((list) =>
                  isPracticeList({ ...list, system: list.system ?? undefined }),
                )
                // A rule list is counted by its rule, the search its table runs. `listMembers` fails outright for
                // some rule lists, and one failing alias empties the whole batch, so manual lists go on their own.
                const ruleCountQuery = buildCountQuery(
                  `ListSidebarRuleCounts`,
                  practiceLists
                    .filter(isFilterList)
                    .map((list) => ({ key: list.id, search: filtersToSearch(listRuleFilters(list), list.join) }))
                    .filter((entry) => entry.search),
                )
                const manualCountQuery = buildListCountQuery(
                  `ListSidebarCounts`,
                  practiceLists.filter((list) => !isFilterList(list)).map((list) => list.id),
                )
                const [ruleCounts, manualCounts] = await Promise.all(
                  [ruleCountQuery, manualCountQuery].map(async (countQuery) => {
                    if (!countQuery) return {}
                    // network-only: a list write returns no typename this query caches, so cache-first would replay old counts.
                    const result = await gqlQuery<Record<string, CountResult>>(countQuery.query, countQuery.variables, {
                      requestPolicy: `network-only`,
                      suppressErrorToast: true,
                    })
                    if (result.data) return readCounts(result.data, countQuery.aliases)
                    logError(`Error fetching practice list counts`, result.error)
                    return undefined
                  }),
                )
                // A failed half keeps its previous counts; a deleted list drops out of both.
                setCounts((prev) => ({
                  ...prev,
                  lists: {
                    ...(ruleCounts ?? pick(prev.lists ?? {}, ruleCountQuery?.aliases.map(({ key }) => key) ?? [])),
                    ...(manualCounts ?? pick(prev.lists ?? {}, manualCountQuery?.aliases.map(({ key }) => key) ?? [])),
                  },
                }))
              }
            } catch (err) {
              logError(`Error fetching practice list counts`, err)
            }
          }
          const activityTagCountablePages = Object.entries(activityTagPages).map(([id, tag], index) => ({
            id,
            alias: `activityTag${index}`,
            search: `${activitiesSearch} ${filtersToSearch([{ attribute: `tags`, value: [tag], operator: `in` } as FilterObject])}`,
          }))
          const activityTagVariables: Record<string, string> = {}
          for (const page of activityTagCountablePages) activityTagVariables[`${page.alias}Search`] = page.search
          const activityTagFields = activityTagCountablePages
            .map((page) => {
              return `${page.alias}: activities(search: $${page.alias}Search, pagination: { limit: 0 }) { total }`
            })
            .join(`\n`)
          const activityTagVariableDefs = activityTagCountablePages
            .map((page) => `$${page.alias}Search: String`)
            .join(`, `)
          const activityTagCountsResult = await gqlQuery<Record<string, CountResult>>(
            // Built per call from a variable list of aliased fields.
            // ast-grep-ignore: no-inline-graphql-document, no-inline-graphql-query
            `query ActivitySidebarTagCounts(${activityTagVariableDefs}) {
          ${activityTagFields}
        }`,
            activityTagVariables,
          )
          const activityTagCounts = activityTagCountsResult.data
          if (activityTagCounts) {
            const activities: Record<string, number> = {}
            for (const page of activityTagCountablePages)
              activities[page.id] = activityTagCounts[page.alias]?.total ?? 0
            setCounts((prev) => ({
              ...prev,
              activities: {
                ...prev.activities,
                ...activities,
              },
            }))
          }
          const pages = await savedListsToCount(`contacts`, CONTACT_CUSTOM_PAGES_QUERY)
          const countablePages = pages
            .map((page, index) => ({
              id: page.id,
              alias: `customContact${index}`,
              search: filtersToSearch(normalizeFilters(page.filters), page.join),
            }))
            .filter((page) => page.search)
          if (countablePages.length) {
            const variables: Record<string, string> = {}
            for (const page of countablePages) variables[`${page.alias}Search`] = page.search
            const fields = countablePages
              .map((page) => {
                return `${page.alias}: contacts(search: $${page.alias}Search, pagination: { limit: 0 }) { total }`
              })
              .join(`\n`)
            const variableDefs = countablePages.map((page) => `$${page.alias}Search: String`).join(`, `)
            const customCountsResult = await gqlQuery<Record<string, CountResult>>(
              // Built per call from a variable list of aliased fields.
              // ast-grep-ignore: no-inline-graphql-document, no-inline-graphql-query
              `query ContactSidebarCustomPageCounts(${variableDefs}) {
            ${fields}
          }`,
              variables,
            )
            const customCounts = customCountsResult.data
            if (customCounts) {
              const customContacts: Record<string, number> = {}
              for (const page of countablePages) customContacts[page.id] = customCounts[page.alias]?.total ?? 0
              setCounts((prev) => ({
                ...prev,
                contacts: {
                  ...prev.contacts,
                  ...customContacts,
                },
              }))
            }
          }
          const activityPages = await savedListsToCount(`activities`, ACTIVITY_CUSTOM_PAGES_QUERY)
          const countableActivityPages = activityPages
            .map((page, index) => ({
              id: page.id,
              alias: `customActivity${index}`,
              search: `${activitiesSearch} ${filtersToSearch(normalizeFilters(page.filters), page.join)}`,
            }))
            .filter((page) => page.search)
          if (countableActivityPages.length) {
            const activityVariables: Record<string, string> = {}
            for (const page of countableActivityPages) activityVariables[`${page.alias}Search`] = page.search
            const activityFields = countableActivityPages
              .map((page) => {
                return `${page.alias}: activities(search: $${page.alias}Search, pagination: { limit: 0 }) { total }`
              })
              .join(`\n`)
            const activityVariableDefs = countableActivityPages.map((page) => `$${page.alias}Search: String`).join(`, `)
            const activityCustomCountsResult = await gqlQuery<Record<string, CountResult>>(
              // Built per call from a variable list of aliased fields.
              // ast-grep-ignore: no-inline-graphql-document, no-inline-graphql-query
              `query ActivitySidebarCustomPageCounts(${activityVariableDefs}) {
            ${activityFields}
          }`,
              activityVariables,
            )
            const customActivityCounts = activityCustomCountsResult.data
            if (customActivityCounts) {
              const customActivities: Record<string, number> = {}
              for (const page of countableActivityPages)
                customActivities[page.id] = customActivityCounts[page.alias]?.total ?? 0
              setCounts((prev) => ({
                ...prev,
                activities: {
                  ...prev.activities,
                  ...customActivities,
                },
              }))
            }
          }
          // Fetch task custom page counts
          const taskPages = await savedListsToCount(`tasks`, TASK_CUSTOM_PAGES_QUERY)
          const countableTaskPages = taskPages
            .map((page, index) => ({
              id: page.id,
              alias: `customTask${index}`,
              search: filtersToSearch(normalizeFilters(page.filters), page.join),
            }))
            .filter((page) => page.search)
          if (countableTaskPages.length) {
            const taskVariables: Record<string, string> = {}
            for (const page of countableTaskPages) taskVariables[`${page.alias}Search`] = page.search
            const taskFields = countableTaskPages
              .map((page) => {
                return `${page.alias}: tasks(search: $${page.alias}Search, pagination: { limit: 0 }) { total }`
              })
              .join(`\n`)
            const taskVariableDefs = countableTaskPages.map((page) => `$${page.alias}Search: String`).join(`, `)
            const taskCustomCountsResult = await gqlQuery<Record<string, CountResult>>(
              // Built per call from a variable list of aliased fields.
              // ast-grep-ignore: no-inline-graphql-document, no-inline-graphql-query
              `query TaskSidebarCustomPageCounts(${taskVariableDefs}) {
            ${taskFields}
          }`,
              taskVariables,
            )
            const customTaskCounts = taskCustomCountsResult.data
            if (customTaskCounts) {
              const customTasks: Record<string, number> = {}
              for (const page of countableTaskPages) customTasks[page.id] = customTaskCounts[page.alias]?.total ?? 0
              setCounts((prev) => ({
                ...prev,
                tasks: {
                  ...prev.tasks,
                  ...customTasks,
                },
              }))
            }
          }
          // Fetch per-pipeline contact counts. Uses the same `pipelines.id:<id>`
          // search the pipelines board uses, so the badge matches the board.
          if (user.workspaceId) {
            const pipelinesResult = await gqlQuery<{
              pipelines: Array<{ id?: string | null; name?: string | null }>
            }>(SIDEBAR_PIPELINES_QUERY)
            const countablePipelines = (pipelinesResult.data?.pipelines ?? [])
              .filter((pipeline) => !isCompanionPipeline({ name: pipeline.name ?? undefined }))
              .map((pipeline) => pipeline.id)
              .filter((id): id is string => !!id)
              .map((id, index) => ({
                id,
                alias: `pipeline${index}`,
                search: `pipelines.id:${id}`,
              }))
            if (countablePipelines.length) {
              const pipelineVariables: Record<string, string> = {}
              for (const pipeline of countablePipelines) pipelineVariables[`${pipeline.alias}Search`] = pipeline.search
              const pipelineFields = countablePipelines
                .map((pipeline) => {
                  return `${pipeline.alias}: contacts(search: $${pipeline.alias}Search, pagination: { limit: 0 }) { total }`
                })
                .join(`\n`)
              const pipelineVariableDefs = countablePipelines
                .map((pipeline) => `$${pipeline.alias}Search: String`)
                .join(`, `)
              const pipelineCountsResult = await gqlQuery<Record<string, CountResult>>(
                // Built per call from a variable list of aliased fields.
                // ast-grep-ignore: no-inline-graphql-document, no-inline-graphql-query
                `query PipelineSidebarCounts(${pipelineVariableDefs}) {
              ${pipelineFields}
            }`,
                pipelineVariables,
              )
              const pipelineCounts = pipelineCountsResult.data
              if (pipelineCounts) {
                const pipelines: Record<string, number> = {}
                for (const pipeline of countablePipelines)
                  pipelines[pipeline.id] = pipelineCounts[pipeline.alias]?.total ?? 0
                setCounts((prev) => ({
                  ...prev,
                  pipelines,
                }))
              }
            }
          }
        })
        .catch((err: unknown) => logError(`Error fetching sidebar counts`, err)),
    )
  }

  /**
   * Ids of the locations that have an inbox. Cached for the lifetime of the
   * provider: entitlement changes land on a page reload, and re-fetching this on
   * every 15s poll would triple the badge's request cost.
   */
  const loadInboxLocationIds = async (): Promise<Set<string>> => {
    if (inboxLocationIdsRef.current) return inboxLocationIdsRef.current
    const { data, error } = await gqlQuery<InboxLocationsResult>(INBOX_LOCATIONS_QUERY, {
      pagination: { limit: 200 },
    })
    // A failed fetch must not poison the cache: caching the empty set here would
    // pin the badge at 0 unread for the rest of the session, since the guard
    // above blocks every retry. Leave the ref empty so the next poll tries again.
    if (error || !data) {
      logError(`Error fetching inbox locations for sidebar badge`, error)
      return new Set()
    }
    const ids = new Set(
      (data.locations?.hits ?? [])
        .filter(hasInboxProduct)
        .map((location) => location.id!)
        .filter(Boolean),
    )
    inboxLocationIdsRef.current = ids
    return ids
  }

  const loadUnreadMessages = () => {
    if (!inboxEnabled) return
    if (unreadFetchInFlightRef.current) return
    unreadFetchInFlightRef.current = true
    // Count unread *threads*, not unread messages — the inbox UI treats a thread as
    // the unit of read/unread state, so the badge should match. We fetch the threadIds
    // of unread inbound messages and dedupe client-side.
    // network-only: this is the polling source of truth for the sidebar badge.
    // Without it, urql's cache-first would replay the same cached count every 2s
    // and the badge would lag by minutes (until some mutation invalidated cache).
    bg(
      (async () => {
        const inboxLocationIds = await loadInboxLocationIds()
        const { data } = await gqlQuery<{
          messages: { hits: Array<UnreadCountableMessage> }
        }>(UNREAD_MESSAGES_QUERY, undefined, { requestPolicy: `network-only` })
        const unreadHits = data?.messages?.hits ?? []
        const suppression = await fetchUnreadOptOutSuppression(unreadHits)
        const serverCount = countUnreadThreads(unreadHits, inboxLocationIds, suppression)
        const prevServer = inboxServerCountRef.current
        inboxServerCountRef.current = serverCount
        // On first poll, seed the display from the server. After that, only
        // apply when the server's value actually shifts — that's the signal
        // for "something real changed" (reindex caught up, external mutation,
        // new customer message, etc.). When the server keeps returning the
        // same stale value, we keep the optimistic local value untouched.
        if (prevServer === null || serverCount !== prevServer) {
          debug(`sidebar-counts`, `Unread inbox threads:`, serverCount)
          setCounts((prev) => ({ ...prev, inbox: serverCount }))
        }
      })()
        .catch(() => {})
        .finally(() => {
          unreadFetchInFlightRef.current = false
        }),
    )
  }

  const refresh = () => {
    loadCounts()
    loadUnreadMessages()
  }

  const adjustInboxCount = (delta: number) => {
    setCounts((prev) => ({ ...prev, inbox: Math.max(0, (prev.inbox ?? 0) + delta) }))
  }

  useEffect(() => {
    loadCounts()
  }, [user?.id, workspace?.id, needsActionDateEnabled, groupedNavigationEnabled])

  // A list's title and its sidebar row show these counts, so a Remove, a Lost move or an add reads them again.
  useEffect(
    () => onSettledListWrite(loadCounts),
    [user?.id, workspace?.id, needsActionDateEnabled, groupedNavigationEnabled],
  )

  // React to Message events on the live stream rather than a private interval, so the badge
  // moves on the same edge that raises the alert card and refreshes the conversation list.
  useMessageEvents(loadUnreadMessages, inboxEnabled)
  useEffect(() => {
    if (inboxEnabled) loadUnreadMessages()
  }, [inboxEnabled])

  return (
    // oxlint-disable-next-line react/jsx-no-constructed-context-values -- context value is intentionally assembled here
    <SidebarCountsContext.Provider value={{ counts, refresh, refreshInboxCount: loadUnreadMessages, adjustInboxCount }}>
      {children}
    </SidebarCountsContext.Provider>
  )
}

export function useSidebarCounts() {
  return useContext(SidebarCountsContext)
}
